import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../hooks';

// Define Crisp globally for TypeScript
declare global {
  interface Window {
    $crisp: any[];
    CRISP_WEBSITE_ID: string;
    CRISP_RUNTIME_CONFIG?: {
      session_merge: boolean;
    };
  }
}

const CRISP_WEBSITE_ID = "49940883-7596-47c1-9664-c5e2d126fd73";

const CrispChat: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const scriptLoaded = useRef(false);
  const crispInitialized = useRef(false);

  // Initialize Crisp only once
  useEffect(() => {
    // If script already loaded, don't load again
    if (scriptLoaded.current) return;
    
    // Initialize Crisp
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
    
    // Enable session merging to maintain persistence across page refreshes
    window.CRISP_RUNTIME_CONFIG = {
      session_merge: true
    };
    
    // Create and append the script
    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    
    // Mark as loaded before appending to prevent double loading
    scriptLoaded.current = true;
    
    document.head.appendChild(script);
    
    // Store Crisp in localStorage to help with persistence
    localStorage.setItem('crisp_loaded', 'true');
    
    // Set an event listener to know when Crisp is fully loaded
    const handleCrispReady = () => {
      crispInitialized.current = true;
      updateUserInfo();
    };
    
    // Check if $crisp.push is available and set the event listener
    if (window.$crisp && typeof window.$crisp.push === 'function') {
      window.$crisp.push(['on', 'session:loaded', handleCrispReady]);
    }
    
    // Clean up function
    return () => {
      // Don't actually remove the script on component unmount
      // This ensures Crisp persists across route changes
      // Instead, just clean up the event listener
      if (window.$crisp && typeof window.$crisp.push === 'function') {
        window.$crisp.push(['off', 'session:loaded', handleCrispReady]);
      }
    };
  }, []);
  
  // Function to update user information
  const updateUserInfo = () => {
    if (!window.$crisp || !Array.isArray(window.$crisp)) return;
    
    try {
      if (isAuthenticated && user) {
        // Set user information
        window.$crisp.push(["set", "user:email", user.email || ""]);
        window.$crisp.push(["set", "user:nickname", user.name || ""]);
        window.$crisp.push(["set", "user:phone", user.phone || ""]);
        
        // Set user ID to maintain session persistence
        window.$crisp.push(["set", "user:token", user._id]);
        
        // Add custom user data that might be helpful for support
        window.$crisp.push(["set", "session:data", [
          ["role", user.role || "user"],
          ["subscription", (user as any).subscriptionTier || "free"],
          ["remaining_passes", user.meetingPasses?.remaining || 0]
        ]]);
      } else {
        // Reset session when logged out
        window.$crisp.push(["do", "session:reset"]);
      }
    } catch (error) {
      console.error("Error updating Crisp user info:", error);
    }
  };
  
  // Update user information when authenticated state or user data changes
  useEffect(() => {
    // Only try to update if Crisp is available
    if (window.$crisp && Array.isArray(window.$crisp)) {
      updateUserInfo();
    }
  }, [isAuthenticated, user]);
  
  // Check for Crisp initialization on window focus (helps with refreshes)
  useEffect(() => {
    const handleWindowFocus = () => {
      // If Crisp isn't initialized but script is loaded, try to update user info
      if (scriptLoaded.current && !crispInitialized.current && window.$crisp) {
        crispInitialized.current = true;
        updateUserInfo();
      }
    };
    
    window.addEventListener('focus', handleWindowFocus);
    
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);
  
  // This component doesn't render anything visible
  return null;
};

export default CrispChat;
